import { DateLabelComponent } from '@/app/components/shared/date-label/date-label.component';
import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { SkeletonMatchesListComponent } from '@/app/components/shared/skeleton';
import { useWsMatchMatches } from '@/app/core/websocket/hooks';
import { IWsMatch } from '@/app/core/websocket/models';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { isSameDay, isToday } from 'date-fns';
import { MatchApi } from '../../../home/models';
import { MatchByCompetitionItemComponent } from '../../components';
import { CompetitionMatchesActiveService } from '../../services';

@Component({
  selector: 'app-matches',
  standalone: true,
  imports: [
    TranslateModule,
    NoResultComponent,
    MatchByCompetitionItemComponent,
    SkeletonMatchesListComponent,
    DateLabelComponent,
  ],
  templateUrl: './matches.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatchesComponent {
  private competitionMatchesActiveService = inject(
    CompetitionMatchesActiveService
  );
  private observer!: IntersectionObserver;
  private wsMatches = useWsMatchMatches();

  route = inject(ActivatedRoute);

  wsMatchesData = signal<Record<string, IWsMatch>>({});

  competitionId = signal('');
  fetchComputed = computed(() => ({
    isLoading:
      this.competitionMatchesActiveService.isLoadingCompetitionsMatchActiveFavorites(),
    isError:
      this.competitionMatchesActiveService.isErrorCompetitionsMatchActiveFavorites(),
  }));
  competitionMatchesActive = computed(() =>
    this.competitionMatchesActiveService.competitionsMatchActive()
  );

  wsDataMatch = effect(
    () => {
      this.wsMatchesData.set(this.wsMatches.data());
    },
    { allowSignalWrites: true }
  );

  scoreContainerRef = viewChild<ElementRef<HTMLDivElement>>(
    'soccerMatchesContainer'
  );
  scrollTriggerRef = viewChild<ElementRef<HTMLDivElement>>('scrollTrigger');

  ngAfterViewInit() {
    // this.performanceService.activate();

    const refItem = this.scrollTriggerRef()?.nativeElement;

    if (!refItem) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            this.competitionMatchesActiveService.isMoreMatches() &&
            this.competitionMatchesActiveService.competitionsMatchActive() !==
              null &&
            this.competitionMatchesActiveService.competitionsMatchActive()!
              .length > 9
          ) {
            this.competitionMatchesActiveService.getCompetitionMatchesActive(
              this.competitionId(),
              this.competitionMatchesActiveService.paginationMatches() + 1
            );
          }
        });
      },
      {
        root: null, // null = viewport
        rootMargin: '200px', // Se activará 200px antes de que el elemento sea visible
        threshold: 0.01,
      }
    );

    this.observer.observe(refItem);

    if (
      !this.competitionMatchesActiveService.isLoadingCompetitionsMatchActiveFavorites()
    ) {
      this.competitionMatchesActiveService.getCompetitionMatchesActive(
        this.competitionId()
      );
    }

    // 👉 Primer fetch con skeleton
    if (
      !this.competitionMatchesActiveService.isLoadingCompetitionsMatchActiveFavorites()
    ) {
      this.competitionMatchesActiveService.getCompetitionMatchesActive(
        this.competitionId()
      );
    }
  }

  constructor() {
    const competitionId = this.route.snapshot.paramMap.get('competitionId');
    if (competitionId) {
      this.competitionId.set(competitionId);

      // this.competitionMatchesActiveService.getCompetitionMatchesActive(
      //   competitionId
      // );
    }
  }

  isNextMatches(date: Date) {
    return !isToday(date);
  }

  isFirstMatchOfDay(matches: MatchApi[], index: number): boolean {
    if (index === 0) return true; // El primer partido siempre muestra la fecha
    const current = matches[index].Date;
    const previous = matches[index - 1].Date;
    return !isSameDay(current, previous);
  }

  ngOnDestroy() {
    this.competitionMatchesActiveService.competitionsMatchActive.set(null);
    this.competitionMatchesActiveService.competitionsMatchPagination.set(null);

    this.wsMatches.disconnect();
  }
}
